import type {
  Product,
  ProductProcessing,
  ProductProcessingError,
  ProductProcessingStage,
  ProductProcessingStageKeyEnum,
} from "@sakura-tech-lab/dashboard-api";

export type IngestionStageKey = ProductProcessingStageKeyEnum;

export interface IngestionStage extends ProductProcessingStage {
  readonly label: string;
  readonly skipped: boolean;
}

export interface IngestionProgress {
  readonly isTracked: boolean;
  readonly stages: readonly IngestionStage[];
  readonly completedCount: number;
  readonly totalCount: number;
  readonly currentStage: IngestionStage | null;
  readonly isComplete: boolean;
  readonly isFailed: boolean;
  readonly isPartial: boolean;
  readonly error: ProductProcessingError | null;
}

const STAGE_LABELS: Record<string, string> = {
  ingest: "Imported",
  image_acquisition: "Images acquired",
  image_indexing: "Images indexed",
  identity_enrichment: "Product identity enriched",
  ai_processing: "AI processing",
  product_profile_extraction: "Product profile extracted",
  product_indexing: "Product indexed",
  deduplication: "Deduplication completed",
};

function hasLocalizedValue(field: Record<string, unknown> | null | undefined): boolean {
  if (!field) return false;
  return Object.values(field).some((value) => {
    if (typeof value === "string") return value.trim().length > 0;
    return value !== null && value !== undefined;
  });
}

function serverStages(processing: ProductProcessing): IngestionStage[] {
  return processing.stages.map((stage) => ({
    ...stage,
    label: STAGE_LABELS[stage.key] ?? stage.key,
    skipped: stage.status === "skipped",
  }));
}

export function getIngestionProgress(
  product: Pick<Product, "processing">,
): IngestionProgress {
  const processing = product.processing;
  const stages = processing ? serverStages(processing) : [];
  const status = processing?.status;
  const isFailed = status === "failed";
  const isPartial = status === "partial";
  const currentStage = processing?.currentStage
    ? stages.find((stage) => stage.key === processing.currentStage) ?? null
    : null;

  return {
    isTracked: processing != null,
    stages,
    completedCount: processing?.completedStages ?? 0,
    totalCount: processing?.totalStages ?? 0,
    currentStage,
    isComplete: status === "completed",
    isFailed,
    isPartial,
    error: processing?.errors[0] ?? null,
  };
}

/** True when at least one variant has an AI-enriched title. */
export function hasEnrichedTitle(product: Pick<Product, "variants">): boolean {
  return (product.variants ?? []).some((variant) => hasLocalizedValue(variant.name));
}

/** True while the selected pipeline still has work in progress. */
export function isIngestionActive(product: Pick<Product, "processing">): boolean {
  const progress = getIngestionProgress(product);
  return progress.isTracked && !progress.isComplete && !progress.isFailed && !progress.isPartial;
}
